import { Link, Navigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { isLegalSlug, LEGAL_PAGES } from "@gaole/shared";
import { api } from "../lib/api";
import { MarkdownContent } from "../components/MarkdownContent";
import { PageLoader } from "../components/ui";

interface LegalContentResponse {
  slug: string;
  title: string;
  body: string;
  updatedAt: string | null;
}

export function LegalPage() {
  const { slug = "" } = useParams<{ slug: string }>();

  if (!isLegalSlug(slug)) {
    return <Navigate to="/" replace />;
  }

  const { data, isLoading, isError } = useQuery({
    queryKey: ["content", slug],
    queryFn: () => api<LegalContentResponse>(`/content/${slug}`),
  });

  if (isLoading) return <PageLoader />;

  if (isError || !data) {
    return (
      <div className="px-4 py-10 text-center">
        <p className="font-display text-sm text-white/70">This page is not available yet.</p>
        <Link to="/" className="mt-3 inline-block text-xs font-semibold text-brand-pink">
          Back to home
        </Link>
      </div>
    );
  }

  return (
    <article className="px-4 py-6">
      <header className="mb-5 border-b border-white/10 pb-4">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-white/40">Legal</p>
        <h1 className="mt-1 font-display text-xl font-bold">{data.title}</h1>
        {data.updatedAt && (
          <p className="mt-1 text-[11px] text-white/40">
            Last updated {new Date(data.updatedAt).toLocaleDateString()}
          </p>
        )}
      </header>
      <MarkdownContent content={data.body} />
      <nav className="mt-8 flex flex-wrap gap-x-4 gap-y-2 border-t border-white/10 pt-4 text-[11px]">
        {LEGAL_PAGES.filter((p) => p.slug !== slug).map((p) => (
          <Link key={p.slug} to={`/legal/${p.slug}`} className="font-medium text-brand-pink">
            {p.title}
          </Link>
        ))}
      </nav>
    </article>
  );
}
